/**
 * Цвет участника.
 *
 * Цвет выводится из идентификатора пользователя, а не хранится в базе:
 * он не является проектными данными, а миграция ради оформления — лишняя
 * сущность. Важнее другое свойство — устойчивость: один и тот же человек
 * всегда одного цвета, в любом проекте и на любом устройстве, потому что
 * идентификатор не меняется.
 *
 * Выдаётся не произвольный оттенок, а один из двенадцати заранее разнесённых
 * по кругу: соседние hue различаются на 30°, и два участника не получат
 * почти одинаковый цвет. При числе участников больше двенадцати совпадения
 * возможны — платформа рассчитана на команду 2–10 человек.
 *
 * Светлота и насыщенность в цвет не входят: их задаёт тема через
 * `--member-l` и `--member-c`, иначе цвет, читаемый на тёмном фоне, слепил бы
 * на кремовом.
 */

/** Двенадцать оттенков по кругу: соседние различимы, противоположные — тем более. */
const HUE_COUNT = 12;
const HUE_STEP = 360 / HUE_COUNT;

/**
 * Устойчивый хеш строки.
 *
 * FNV-1a: короткий, без зависимостей и без коллизий на UUID в пределах,
 * которые здесь имеют значение.
 */
function hash(value: string): number {
  let result = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    result ^= value.charCodeAt(index);
    result = Math.imul(result, 0x01000193);
  }
  return result >>> 0;
}

/** Оттенок участника в градусах. */
export function memberHue(userId: string): number {
  return (hash(userId) % HUE_COUNT) * HUE_STEP;
}

/**
 * Стиль-переменные для элемента, окрашенного по участнику.
 *
 * Возвращается объект для `style`, а сами цвета собираются в CSS: так тема
 * управляет светлотой, а компонент — только оттенком.
 *
 *   <span className="member-dot" style={memberColorStyle(user.id)} />
 */
export function memberColorStyle(userId: string | null | undefined): React.CSSProperties {
  if (!userId) return {};
  return { '--member-h': `${memberHue(userId)}` } as React.CSSProperties;
}
